import {
  DatasetQueryFragment,
  useLoadDatasetQueriesQuery,
} from '@warebee/frontend/data-access-api-graphql';
import _ from 'lodash';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { cn } from '../common/utils';
import DropdownSelector from '../components/actions/DropdownSelector';
import ErrorIndicator from '../components/ErrorIndicator';
import { ScreenTitle } from '../components/layout/ScreenTitle';
import LoadingIndicator from '../components/LoadingIndicator';
import { warehouseSelectedId } from '../store/warehouse.state';

interface DatasetQuerySelectorProps {
  selectedId: string;
  onChange: (datasetQuery: DatasetQueryFragment) => void;
  viewMode?: 'header' | 'table' | 'dashboard' | 'panel' | 'minimal';
}

const DatasetQuerySelector: FC<DatasetQuerySelectorProps> = props => {
  const { t } = useTranslation('dataset');
  const { viewMode } = props;
  const whId = useRecoilValue(warehouseSelectedId);

  const { data, loading, error } = useLoadDatasetQueriesQuery({
    variables: {
      warehouseId: whId,
      page: { skip: 0, limit: 1000 },
    },
  });

  const dataMap = _.keyBy(
    data?.warehouse?.datasetQueries?.content,
    ds => ds.id,
  );

  const handleReset = () => {
    props.onChange(null);
  };

  return (
    <div data-component={`DataSettingSelector`}>
      {props.viewMode !== 'minimal' && (
        <ScreenTitle
          title={t`Query`}
          subtitle={t``}
          // icon={}
          isSticky
          className="top-8 px-2"
        />
      )}

      {loading && (
        <LoadingIndicator oneLiner message={t('Loading Dataset Queries')} />
      )}
      {error && (
        <ErrorIndicator message={t('Loading Dataset Queries')} selfCenter />
      )}

      {!loading && !error && (
        <div
          className={cn(
            'flex flex-col items-center',
            props.viewMode === 'minimal' ? '' : 'bg-sidebar-title/75 px-2 py-4',
          )}
        >
          <div
            className={cn(
              'group/data-selector',
              'flex items-center',
              'gap-2',
              'mb-2 w-full',
            )}
          >
            <DropdownSelector
              label={viewMode === 'minimal' ? 'Saved Queries' : null}
              className={cn(
                'min-w-0 flex-1 transform text-sm transition-all duration-200',
              )}
              value={props.selectedId || t('No Data Selected')}
              values={_.keys(dataMap)}
              renderValue={(id: string) => dataMap[id]?.title || id}
              valueHelper={(id: string) =>
                dataMap[id]?.updatedAt
                  ? new Date(dataMap[id]?.updatedAt).toLocaleString('en-GB')
                  : ''
              }
              onChange={id => props.onChange(dataMap[id])}
              panelMode
              DropAlignRight
              widthFull
              valuePrimary
              // multiline
              light
              hasSearch
              hasSearchLabel={t(`Search`)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DatasetQuerySelector;
